/**
 * Org context: current organization and memberships with permissions.
 * Used by App (setMembershipsWithPermissions), OrgSwitcher, Ops/Studio pages, and opsV2 client.
 */

const STORAGE_KEY_ORG = "xlflow_current_org_id";

export type MembershipWithPermissions = {
  org_id: string;
  org_type?: string;
  /** Liste effective (après normalisation depuis /me ou /auth/sync). */
  permissions?: string[];
  /** Alias renvoyé par l’API ; fusionné dans `permissions` par setMembershipsWithPermissions. */
  effective_permissions?: string[];
  [key: string]: unknown;
};

let membershipsWithPermissions: MembershipWithPermissions[] = [];
const listeners: Array<() => void> = [];

function getStoredOrgId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY_ORG);
  } catch {
    return null;
  }
}

function setStoredOrgId(orgId: string | null): void {
  try {
    if (orgId == null) {
      window.localStorage.removeItem(STORAGE_KEY_ORG);
    } else {
      window.localStorage.setItem(STORAGE_KEY_ORG, orgId);
    }
  } catch {
    // ignore
  }
}

function notifyListeners(): void {
  listeners.forEach((cb) => cb());
}

export function setMembershipsWithPermissions(memberships: MembershipWithPermissions[]): void {
  const raw = Array.isArray(memberships) ? memberships : [];
  membershipsWithPermissions = raw.map((m) => {
    const mAny = m as Record<string, unknown>;
    const orgId = String(mAny.org_id ?? mAny.organization_id ?? "");
    const permList = Array.isArray(mAny.permissions)
      ? (mAny.permissions as string[])
      : Array.isArray(mAny.effective_permissions)
        ? (mAny.effective_permissions as string[])
        : [];
    return {
      ...m,
      org_id: orgId,
      permissions: permList,
    };
  });
  const stored = getStoredOrgId();
  if (stored && !membershipsWithPermissions.some((m) => m.org_id === stored)) {
    setStoredOrgId(null);
  }
  if (!getStoredOrgId() && membershipsWithPermissions.length > 0) {
    setStoredOrgId(membershipsWithPermissions[0].org_id);
  }
  notifyListeners();
}

export function getMembershipsWithPermissions(): MembershipWithPermissions[] {
  return membershipsWithPermissions;
}

export function getCurrentOrgId(): string | null {
  const stored = getStoredOrgId();
  if (stored && membershipsWithPermissions.some((m) => m.org_id === stored)) {
    return stored;
  }
  if (membershipsWithPermissions.length > 0) {
    return membershipsWithPermissions[0].org_id;
  }
  return null;
}

export function setCurrentOrg(orgId: string): void {
  if (membershipsWithPermissions.some((m) => m.org_id === orgId)) {
    setStoredOrgId(orgId);
    notifyListeners();
  }
}

export function getCurrentOrgType(): string | null {
  const id = getCurrentOrgId();
  if (!id) return null;
  const m = membershipsWithPermissions.find((x) => x.org_id === id);
  return m?.org_type ?? null;
}

export function hasPermissionInCurrentOrg(permission: string): boolean {
  const id = getCurrentOrgId();
  if (!id) return false;
  const m = membershipsWithPermissions.find((x) => x.org_id === id);
  const perms = m?.permissions ?? m?.effective_permissions;
  if (!Array.isArray(perms)) return false;
  return perms.includes(permission);
}

export function subscribeToOrgContext(callback: () => void): () => void {
  listeners.push(callback);
  return () => {
    const i = listeners.indexOf(callback);
    if (i >= 0) listeners.splice(i, 1);
  };
}
